<template>
  <div>
    <div class="text-center mt-6" v-if="!dataLoaded">
      <v-progress-circular :size="50" color="primary" indeterminate />
    </div>

    <div v-show="dataLoaded">
      <v-row class="my-0">
        <v-col cols="8" class="pr-2 py-0">
          <v-card
            ref="holidayInfo"
            class="base-card holiday-info pa-5"
            :disabled="loading"
          >
            <div>
              <h4 class="holiday-info-title">Выходные дни:</h4>

              <p class="no-holiday" v-if="!holidays.datesList.length">
                Выходные дни не выбраны
              </p>

              <div class="holiday-list" v-else>
                <table class="base-table">
                  <tr v-for="date in holidays.datesList">
                    <td>{{ date | getDateWithMonthName }}</td>
                    <td>{{ date | getDayName }}</td>
                    <td>
                      <v-icon
                        color="lightGrey"
                        class="remove"
                        size="22"
                        title="Удалить"
                        @click="removeHoliday(date)"
                      >
                        mdi-trash-can-outline
                      </v-icon>
                    </td>
                  </tr>
                </table>
              </div>

              <div class="bottom-space"></div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="4" class="pl-2 py-0">
          <v-card class="base-card pa-3" :disabled="loading">
            <v-date-picker
              ref="holidayCalendar"
              :value="holidays.datesList"
              class="holiday-calendar"
              locale="ru"
              color="primary"
              full-width
              landscape
              no-title
              multiple
              :show-current="false"
              :min="minDate"
              :max="maxDate"
              :first-day-of-week="1"
              :picker-date="startMonth"
              @input="changeHolidaysCalendar"
            />
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { holidayApi } from '@/api'
import { mapGetters } from 'vuex'
import { formatDateToISO, getDateWithMonthName, getDayName } from '@/scripts'
import { Holidays, Holiday } from '@/models'

export default {
  name: 'Holidays',

  metaInfo: {
    title: 'Выходные дни'
  },

  async created() {
    await this.getHolidays()
    this.dataLoaded = true
  },

  mounted() {
    this.setTableHeight()
    window.addEventListener("resize", this.setTableHeight)
  },

  destroyed() {
    window.removeEventListener("resize", this.setTableHeight)
  },

  data: () => ({
    dataLoaded: false,
    startMonth: null,
    loading: false,
    minDate: formatDateToISO(new Date()),
    maxDate: formatDateToISO(new Date(new Date().getFullYear(), 11, 31)),

    holidays: new Holidays()
  }),

  computed: {
    ...mapGetters('system', ['techMessageHeight'])
  },

  filters: {
    getDayName,
    getDateWithMonthName
  },

  methods: {
    async getHolidays() {
      try {
        // показываем стартовый месяц календаря, равный дате начала цикла
        this.startMonth = this.minDate

        const data = await holidayApi.getHolidays()
        this.holidays.buildFromAPI(data)

        // обнуляем стартовый месяц календаря, чтобы не блочилось перелистывание месяца
        this.startMonth = null
      } catch (e) {
        console.log(e.status)
      }
    },

    changeHolidaysCalendar(dates) {
      const addDate = dates.find(date => !this.holidays.datesList.includes(date))
      const removeDate = this.holidays.datesList.find(date => !dates.includes(date))

      if (addDate) {
        const holiday = new Holiday()
        holiday.setDate(addDate)
        this.addHoliday(holiday)
      } else if (removeDate) {
        this.removeHoliday(removeDate)
      }
    },

    async addHoliday(holiday) {
      try {
        this.loading = true

        const data = await holidayApi.sendHoliday(holiday.getForAPI())
        this.holidays.addHoliday(data)
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка сохранения выходного дня')
      } finally {
        this.loading = false
      }
    },

    async removeHoliday(date) {
      try {
        this.loading = true
        const holiday = this.holidays.list.find(el => el.date === date)

        if (holiday.id) {
          await holidayApi.removeHoliday(holiday.id)
          this.holidays.removeHoliday(holiday.id)
        }
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка удаления выходного дня')
      } finally {
        this.loading = false
      }
    },

    setTableHeight() {
      const otherElements = 261 + this.techMessageHeight

      if (this.$refs?.holidayInfo) {
        this.$refs.holidayInfo.$el.style.maxHeight = window.innerHeight - otherElements + 'px'
      }
    }
  }
}
</script>

<style scoped>
/* область внизу карточки, чтобы визуально везде был padding */
.base-card .bottom-space {
  position: sticky;
  bottom: 0;
  height: 20px;
  width: 100%;
  background-color: #fff;
}

.holiday-calendar >>> .v-date-picker-table--date {
  padding-left: 0;
  padding-right: 0;
}

.holiday-calendar >>> .v-date-picker-header {
  padding: 0 8px;
}

.holiday-calendar >>> .v-date-picker-header .theme--light.v-btn.v-btn--disabled {
  background-color: #fff !important;
}

.holiday-calendar >>> .v-date-picker-header .theme--light.v-btn.v-btn--disabled .v-icon {
  color: var(--disabled-color) !important;
}

.holiday-calendar >>> .v-date-picker-table {
  height: 248px;
}

.holiday-calendar >>> .v-date-picker-table th {
  color: var(--accent-color) !important;
}

.holiday-calendar >>> .theme--light.v-btn {
  color: var(--accent-color) !important;
}

.holiday-calendar >>> .theme--light.v-btn.v-btn--disabled {
  color: var(--disabled-color) !important
}

.holiday-calendar >>> .theme--light.v-btn.v-btn--active {
  color: #fff !important
}

.holiday-calendar >>> .v-date-picker-header__value button {
  color: var(--accent-color) !important;
}

.holiday-info {
  color: var(--accent-color) !important;
  min-height: 312px;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  overflow-y: auto;
}

.holiday-info-title {
  position: sticky;
  line-height: 1;
  padding-top: 20px;
  padding-bottom: 20px;
  top: 0;
  z-index: 1;
  background-color: #fff;
}

.no-holiday {
  margin-bottom: 0;
  margin-top: 6px;
  font-size: 15px;
}

.holiday-list table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.holiday-list table tr td {
  border-bottom: 1px solid var(--separator-color);
  padding: 7px 0;
}

.holiday-list table tr:last-child td {
  border-bottom: none;
}

.holiday-list table td:first-child {
  width: 150px;
}

.holiday-list table td:last-child {
  text-align: right;
}

.holiday-list .remove:hover {
  color: var(--error-color) !important;
}
</style>